import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { officeWriteBlock, type OfficeWriteBlock } from '../utils/officeAccess'
import type { ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { useLang } from './LangContext'
import type { Lang } from './LangContext'
import type { PlanKey } from '../config/plans'
import { setSentryOfficeContext } from '../lib/sentry'

interface OfficeState {
  officeId: string | null
  officeName: string
  officePlan: PlanKey
  officeStatus: string
  trialEndsAt: string | null
  /** ISO timestamp when access expires on a scheduled cancellation, null
   *  otherwise. This is the signal that a cancellation is scheduled: the status
   *  stays 'active' because office_status has no value for it. Drives the
   *  countdown banner and reactivation. */
  accessUntil: string | null
  /** True when invoice.payment_failed and Stripe still has retries scheduled.
   *  Shows amber warning banner but does NOT restrict access. */
  paymentPastDue: boolean
  /** Why the office cannot write right now (the database's is_office_active rule),
   *  or null when it can. Screens disable actions and say why; never offer a write
   *  the database will refuse. */
  writeBlock: OfficeWriteBlock | null
  /** Shorthand for writeBlock === null. */
  officeWritable: boolean
  userRole: 'admin' | 'agent' | null
  loading: boolean
  onboardingCompleted: boolean | null
  /** True when the authenticated user has no active office membership.
   *  Distinct from onboardingCompleted=false (new user) — this means the
   *  user existed before but their access was revoked by an admin. */
  noOffice: boolean
  unreadAlerts: number
  decrementAlertCount: () => void
  refreshAlertCount: () => void
  refreshPlan: () => Promise<{ plan: string; status: string } | null>
  refreshOfficeName: () => Promise<void>
  dataVersion: number
  notifyDataChanged: () => void
  refreshOnboarding: () => void
  markOnboardingDone: () => void
}

async function withRetry<T>(fn: () => Promise<T>, maxAttempts = 3, baseDelayMs = 1000): Promise<T> {
  let lastError: Error | null = null
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt - 1)))
      }
    }
  }
  throw lastError
}

const OfficeContext = createContext<OfficeState | undefined>(undefined)

export function OfficeProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { applyFromMetadata: applyLang } = useLang()
  const [officeId, setOfficeId] = useState<string | null>(null)
  const [officeName, setOfficeName] = useState('')
  const [officePlan, setOfficePlan] = useState<PlanKey>('trial')
  const [officeStatus, setOfficeStatus] = useState('trial')
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null)
  const [accessUntil, setAccessUntil] = useState<string | null>(null)
  const [paymentPastDue, setPaymentPastDue] = useState(false)
  const [userRole, setUserRole] = useState<'admin' | 'agent' | null>(null)
  const [loading, setLoading] = useState(true)
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null)
  const [noOffice, setNoOffice] = useState(false)
  const [unreadAlerts, setUnreadAlerts] = useState(0)
  const [dataVersion, setDataVersion] = useState(0)

  const notifyDataChanged = useCallback(() => {
    setDataVersion((v) => v + 1)
  }, [])

  const refreshOnboarding = useCallback(async () => {
    if (!officeId) return
    const { data } = await supabase
      .from('offices')
      .select('onboarding_completed')
      .eq('id', officeId)
      .single()
    setOnboardingCompleted(data?.onboarding_completed ?? false)
  }, [officeId])

  const markOnboardingDone = useCallback(() => {
    setOnboardingCompleted(true)
  }, [])

  // Instantly decrement badge without waiting for a round-trip to the DB.
  // Used by resolveAlert so the sidebar number drops to zero immediately.
  const decrementAlertCount = useCallback(() => {
    setUnreadAlerts((prev) => Math.max(0, prev - 1))
  }, [])

  const refreshAlertCount = useCallback(async () => {
    if (!officeId) return
    const { count } = await supabase
      .from('office_alerts')
      .select('*', { count: 'exact', head: true })
      .eq('office_id', officeId)
      .is('resolved_at', null)   // active = unresolved, regardless of is_read — viewing an
                                  // alert (Alerts.tsx marks is_read on click) must not hide it
    setUnreadAlerts(count ?? 0)
  }, [officeId])

  const refreshOfficeName = useCallback(async () => {
    if (!officeId) return
    const { data } = await supabase
      .from('offices')
      .select('name')
      .eq('id', officeId)
      .single()
    if (data?.name) setOfficeName(data.name)
  }, [officeId])

  const refreshPlan = useCallback(async () => {
    if (!officeId) return null
    const { data } = await supabase
      .from('offices')
      .select('plan, status, trial_ends_at, payment_past_due_since, access_until')
      .eq('id', officeId)
      .single()
    if (data) {
      setOfficePlan(data.plan as PlanKey)
      setOfficeStatus(data.status)
      setTrialEndsAt(data.trial_ends_at ?? null)
      setAccessUntil((data as Record<string, unknown>).access_until as string | null ?? null)
      setPaymentPastDue(!!data.payment_past_due_since)
    }
    return data ? { plan: data.plan as string, status: data.status as string } : null
  }, [officeId])

  useEffect(() => {
    // Wait for AuthContext to finish its own session check first. Without this,
    // `user` reads as null during that initial window (indistinguishable from
    // "really logged out"), so this effect used to bail out and mark office
    // loading done prematurely — on a hard page reload of an admin-only route
    // (e.g. /audit-log), ProtectedRoute would then render the page with a
    // stale userRole=null before the real fetch below ever ran, and the
    // page's own admin-only guard would redirect away incorrectly.
    if (authLoading) return
    if (!user) {
      setLoading(false)
      return
    }

    async function fetchOffice() {
      const data = await withRetry(async () => {
        const res = await supabase
          .from('office_users')
          .select('office_id, role, offices(name, preferred_lang, onboarding_completed, plan, status, trial_ends_at, payment_past_due_since, access_until)')
          .eq('user_id', user!.id)
          .eq('status', 'active')   // only active memberships — removed users get no office
          .order('created_at', { ascending: false })
        if (res.error) throw res.error
        // The product is single-office-per-user (the invite flow blocks joining a
        // second office). But a stray dual-membership must NEVER white-screen the
        // app: .maybeSingle() throws on >1 row, and the catch below would then
        // leave officeId null → blank Overview, Settings won't open. So we fetch
        // all active memberships and pick one deterministically: prefer an admin
        // role, else the most recent. Returns null when there are none.
        const rows = res.data ?? []
        return rows.find((r) => r.role === 'admin') ?? rows[0] ?? null
      })

      if (data) {
        const oid = data.office_id
        const office = data.offices as unknown as {
          name: string
          preferred_lang: string | null
          onboarding_completed: boolean | null
          plan: string | null
          status: string | null
          trial_ends_at: string | null
          payment_past_due_since: string | null
          access_until: string | null
        }
        setOfficeId(oid)
        setOfficeName(office.name)
        setUserRole(data.role as 'admin' | 'agent')
        setOnboardingCompleted(office.onboarding_completed ?? false)
        setOfficePlan((office.plan ?? 'trial') as PlanKey)
        setOfficeStatus(office.status ?? 'trial')
        setTrialEndsAt(office.trial_ends_at ?? null)
        setAccessUntil(office.access_until ?? null)
        setPaymentPastDue(!!office.payment_past_due_since)
        // applyFromMetadata: sets state/localStorage only, no Supabase call (avoids 429 loop)
        // offices.preferred_lang is the language the server writes the office's e-mails
        // and alerts in. The interface follows the person's own choice (user_metadata.lang,
        // applied by UserPrefsSync); the office's language only fills in for someone who
        // never chose one.
        if (office.preferred_lang && !user!.user_metadata?.lang) applyLang(office.preferred_lang as Lang)
      } else {
        // No active office membership found.
        // Distinguish between a brand-new user (just signed up, no office yet)
        // and a removed user (had access, was revoked by admin).
        // Heuristic: if the auth account is > 2 hours old, they're not fresh —
        // they were removed. New users complete onboarding within minutes.
        const TWO_HOURS_MS = 2 * 60 * 60 * 1000
        const createdAt = user!.created_at ? new Date(user!.created_at).getTime() : Date.now()
        const isOldAccount = Date.now() - createdAt > TWO_HOURS_MS
        if (isOldAccount) {
          setNoOffice(true)
        } else {
          setOnboardingCompleted(false)
        }
      }
      setLoading(false)
    }

    fetchOffice().catch((err) => {
      console.error('[OfficeContext] fetchOffice failed after retries', err)
      setLoading(false)
    })
  }, [user, authLoading])

  useEffect(() => {
    if (!officeId) return
    refreshAlertCount()

    const channel = supabase
      .channel('alerts-count')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'office_alerts',
        filter: `office_id=eq.${officeId}`,
      }, () => { refreshAlertCount() })
      .subscribe()

    // Backstop for the realtime subscription above: office_alerts was never added
    // to a realtime publication (no migration does it), so postgres_changes here
    // silently never fires — the badge only reflected reality at the moment this
    // effect first mounted. Backend-created alerts (the USCIS poller, financial
    // due-date checks) have no client connected to notify, so without a poll the
    // sidebar count can sit stale indefinitely on an already-open tab.
    const poll = setInterval(refreshAlertCount, 60_000)

    return () => { supabase.removeChannel(channel); clearInterval(poll) }
  }, [officeId, dataVersion, refreshAlertCount])

  // Push tenant context into Sentry whenever it changes (login, office switch,
  // logout). Lets us filter errors by office in the Sentry dashboard — critical
  // for triaging "is this affecting one tenant or all of them?".
  useEffect(() => {
    setSentryOfficeContext({
      userId:     user?.id ?? null,
      officeId,
      officePlan: officePlan ?? null,
      userRole,
    })
  }, [user?.id, officeId, officePlan, userRole])

  const writeBlock = officeWriteBlock(officeStatus, officePlan, trialEndsAt)

  return (
    <OfficeContext.Provider value={{ officeId, officeName, officePlan, officeStatus, trialEndsAt, accessUntil, paymentPastDue, writeBlock, officeWritable: writeBlock === null, userRole, loading, onboardingCompleted, noOffice, unreadAlerts, decrementAlertCount, refreshAlertCount, refreshPlan, refreshOfficeName, dataVersion, notifyDataChanged, refreshOnboarding, markOnboardingDone }}>
      {children}
    </OfficeContext.Provider>
  )
}

export function useOffice() {
  const context = useContext(OfficeContext)
  if (!context) throw new Error('useOffice must be used within OfficeProvider')
  return context
}
